import React, { useEffect, useState, useRef, memo, useCallback } from "react";
import Image from 'next/legacy/image';
import PuffLoader from "react-spinners/PuffLoader";
import {
    XYPlot,
    GradientDefs,
    AreaSeries,
    VerticalBarSeries,
    FlexibleWidthXYPlot,
    MarkSeries,
    XAxis,
} from 'react-vis';
import {
    Modal,
    ModalOverlay,
    ModalHeader,
    ModalBody,
    ModalContent,
    ModalCloseButton,
    ModalFooter,
    Button,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    useColorModeValue,
    Box,
    Heading,
    Text,
    useMediaQuery,
    Center,
    Flex,
    Spacer
} from "@chakra-ui/react";
import numbro from "numbro";
import { MdOutlineContentCopy } from "react-icons/md";
import { HiOutlineDownload } from "react-icons/hi";
import DomToImage from "dom-to-image";
import toast from "react-hot-toast";
import { yDomain } from "components/analytics/utils";
import { ModalInfoIcon } from "./ModalInfoIcon";
import { variant } from "theme/names";

import moment from "moment";

export const StatScreenshot = ({ isOpen, onClose, screenshotOf = "big_graph", data = {} as any, forAccount = "", previousDayStat = [] }) => {

    if (!data || Object.keys(data).length == 0) return null;

    const [screenshotType, setScreenshotType] = useState(screenshotOf);
    const [graphData, setGraphData] = useState(data?.stats);
    const [graphTitle, setGraphTitle] = useState(data?.type);
    const [graphLabelValue, setGraphLabelValue] = useState(data?.labelValue);
    const [graphPercentageValue, setGraphPercentageValue] = useState(data?.percentageValue);
    const [accountName, setAccountName] = useState(forAccount);
    const screenshotRef: any = useRef(null);
    const [isGeneratingCopy, setIsGeneratingCopy] = useState(false);
    const [isGeneratingDownload, setIsGeneratingDownload] = useState(false);
    const [bigGraphType, setBigGraphType] = useState(data?.graphType || "line");
    const [isTooSmall] = useMediaQuery("(max-width: 840px)");
    const [pngBlob, setPngBlob] = useState<any>();
    const [imageURL, setImageURL] = useState<any>();
    const [isCompLoaded, setIsCompLoaded] = useState<boolean>(false);

    useEffect(() => {
        setScreenshotType(screenshotOf);
        setGraphData([...data?.stats]);
        setGraphTitle(data?.type);
        setGraphLabelValue(data?.labelValue);
        setGraphPercentageValue(data?.percentageValue);
        setAccountName(forAccount);
        setBigGraphType(data?.graphType || "line");
    }, [screenshotOf, data, forAccount, previousDayStat]);

    const generateScreenshotImage = async () => {
        const blob = await DomToImage.toBlob(screenshotRef.current, {
            height: 630,
            width: 1200,
            quality: 1,
            style: {
                width: 1200 + "px",
                height: 630 + "px",
                display: "flex",
                flexDirection: "column",
                margin: "auto",
                justifyContent: "center"
            }
        });

        const urlCreator = window.URL || window.webkitURL;
        blob && setImageURL(urlCreator?.createObjectURL(blob));
        setPngBlob(blob);
        setIsCompLoaded(true);
    }

    const closePopup = async () => {
        onClose();
    }

    return (
        <Modal isOpen={isOpen} onClose={closePopup} size={(isTooSmall) ? "lg" : "xl"}>
            <ModalOverlay />
            <ModalContent maxW={(isTooSmall) ? "100%" : 900} p={{ base: 4, md: 6 }}  >
                <ModalHeader display={"flex"} flexDirection={"column"} justifyContent={"start"} alignItems={"start"}>
                    <Heading fontSize={"2xl"} mb={2}>
                        Share your stats on LinkedIn
                    </Heading>
                    <Text fontSize={"lg"}>
                        The image has been optimized to display perfectly in a LinkedIn post.
                    </Text>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody w={['100%', "auto"]}>
                    <Center>
                        <PuffLoader
                            color={"#2C5282"}
                            loading={!isCompLoaded}
                            cssOverride={{ display: "block", margin: "0 auto", borderColor: "red" }}
                            size={50}
                        />
                    </Center>
                    {
                        isCompLoaded && imageURL &&
                        <Image
                            src={imageURL}
                            width={1200}
                            height={630}
                            layout={"responsive"}
                        />
                    }
                    {
                        (screenshotType === "stats_box_graph")
                            ?
                            (
                                <Box as="div" display={"none"} w={1200} h={630}>
                                    <Box
                                        h={630}
                                        w={1200}
                                        bgGradient="linear(to-br, #70E1F5, #8E44AD)"
                                        display={"flex"}
                                        flexDirection={"column"}
                                        placeItems={"center"}
                                        ref={screenshotRef}
                                        pt={3}
                                    >
                                        <StatsBoxScreenshotMemoed
                                            title={graphTitle}
                                            labelValue={graphLabelValue}
                                            boxPercentageValue={graphPercentageValue}
                                            data={[...graphData]}
                                            squareDim={420}
                                            onLoad={() => {
                                                setIsCompLoaded(false);
                                                setTimeout(() => {
                                                    //needed for refs, etc to upate
                                                    screenshotRef.current && generateScreenshotImage();
                                                }, 1000);
                                            }}
                                        />
                                        <Text mt={1} mb={"auto"} bg={"gray.900"} borderRadius={20} px={5} py={2} fontSize={["md", "lg"]} color={"white"} align="center">
                                            Stats for {accountName}, certified by Taplio.com
                                        </Text>
                                    </Box>
                                </Box>
                            )
                            :
                            (
                                <Box as="div" w={1200} h={630} display="none">
                                    <Box
                                        h={630}
                                        w={1200}
                                        bgGradient="linear(to-br, #70E1F5, #8E44AD)"
                                        display={"flex"}
                                        flexDirection={"column"}
                                        placeItems={"center"}
                                        ref={screenshotRef}
                                        pt={3}
                                    >
                                        <BigGraphScreenshotMemoed
                                            graphType={bigGraphType}
                                            graphData={graphData}
                                            graphOf={graphTitle}
                                            onLoad={() => {
                                                setIsCompLoaded(false);
                                                setTimeout(() => {
                                                    //needed for refs, etc to upate
                                                    screenshotRef.current && generateScreenshotImage();
                                                }, 1000);
                                            }}
                                            previousDayStat={previousDayStat}
                                        />
                                        <Text mt={1} mb={"auto"} bg={"gray.900"} borderRadius={20} px={5} py={2} fontSize={"lg"} color={"white"} align="center">
                                            Stats for {accountName}, certified by Taplio.com
                                        </Text>
                                    </Box>
                                </Box>
                            )
                    }
                </ModalBody>

                <ModalFooter
                    display={"flex"}
                    flexDirection={"row"}
                    overflow={"hidden"}
                    justifyContent={"center"}
                    mt={6}
                >
                    <Button
                        // colorScheme="gray"
                        variant={variant.Button.primary}
                        leftIcon={<MdOutlineContentCopy />}
                        mr={6}
                        isLoading={isGeneratingCopy}
                        onClick={async () => {
                            setIsGeneratingCopy(true);
                            try {
                                //const scale = window.devicePixelRatio;
                                await navigator.clipboard.write([
                                    //@ts-ignore
                                    new ClipboardItem({ "image/png": pngBlob as Blob }),
                                ]);
                                toast.success("Screenshot Copied to Clipboard");
                            } catch (e) {
                                toast.error("An error occured while copying");
                                console.log(e);
                            }
                            setIsGeneratingCopy(false);
                        }}
                    >
                        Copy to Clipboard
                    </Button>
                    <Button
                        variant={variant.Button.primary}
                        leftIcon={<HiOutlineDownload />}
                        isLoading={isGeneratingDownload}
                        onClick={async () => {
                            setIsGeneratingDownload(true);
                            try {
                                //const scale = window.devicePixelRatio;
                                let a = document.createElement("a");
                                a.href = imageURL;
                                a.download = "taplio-stats-screenshot.png";
                                a.click();
                                a.remove();
                                toast.success("Screenshot Generated, Starting download...");
                            } catch (e) {
                                toast.error("An error occured while copying");
                            }

                            setTimeout(() => {
                                setIsGeneratingDownload(false);
                            }, 210);
                        }}
                    >
                        Download Image
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

const StatBoxScreenshot = ({ title = "", labelValue = 0, boxPercentageValue = 0, data = [] as any, squareDim = 300, onLoad = () => { } }) => {
    const findNumSign = (num) => (num > 0) ? "+" : "";

    useEffect(() => {
        onLoad();
    }, []);

    return (
        <Box
            display="flex"
            flexDirection="column"
            bg={"transparent"}
            placeItems={"center"}
            m={"auto"}
            rounded={"2xl"}
            position="relative"
        >
            <Stat
                width={squareDim}
                height={squareDim}
                maxH={squareDim}
                margin={"auto"}
                mb={6}
                overflow="hidden"
                p={6}
                pb={0}
                borderRadius={20}
                boxShadow={"1px 1px 1px 0px #ececec"}
                bg={"white"}
                display={"flex"}
                flexDirection={"column"}
            >
                <StatLabel
                    fontSize={"4xl"}
                    fontWeight="900"
                    textTransform="uppercase"
                    mb={10}
                    letterSpacing="3.21px"
                    color={"gray.700"}
                >
                    <Box fontSize={"12px"} p={0} ml={"1px"} mb={5} color={"gray.600"}>
                        {data?.[0]?.date} - {data?.[data?.length - 1]?.date}
                    </Box>
                    {title}

                </StatLabel>
                <StatNumber
                    fontSize={"4xl"}
                    fontWeight="600"
                    mb={10}
                    color={"#494949"}
                >
                    {numbro(labelValue).format({
                        thousandSeparated: true,
                        mantissa: 0
                    })}
                </StatNumber>
                <Box
                    fontSize={"4xl"}
                    fontWeight="900"
                    textTransform="uppercase"
                    color={(findNumSign(boxPercentageValue) == "+") ? "#099e76" : "#8511BB"}
                    mb={3}
                    alignSelf={"end"}
                >
                    {findNumSign(boxPercentageValue)}{boxPercentageValue}%
                    <Box
                        as="span"
                        fontWeight="200"
                        ml={1}
                        fontSize={"xl"}
                        color="#909090"
                        textTransform="lowercase"
                    >
                        &nbsp; VS. Prev. Period
                    </Box>
                </Box>

                <StatHelpText p={0} m={0} mt={0} >
                    <XYPlot
                        stackBy="y"
                        width={369}
                        height={150}
                        margin={{ left: 0, right: 0, top: 0, bottom: 0 }}
                        yDomain={yDomain(data)}
                    >
                        <GradientDefs>
                            <linearGradient id="CoolGradient" x1="0" x2="0" y1="0" y2="1">
                                <stop offset="0%" stopColor="#11BB8D" stopOpacity={0.7} />
                                <stop offset="100%" stopColor="#93eed5" stopOpacity={0.2} />
                            </linearGradient>
                        </GradientDefs>
                        <VerticalBarSeries
                            color="#11BB8D"
                            data={data}
                            opacity={1}
                            style={{
                                fill: "url(#CoolGradient)",
                                stroke: '#11BB8D',
                                strokeWidth: '0px',
                                cursor: "pointer",
                            }}
                            barWidth={0.81}
                        />
                    </XYPlot>
                </StatHelpText>
            </Stat>
        </Box>
    )
}

const StatsBoxScreenshotMemoed = memo(StatBoxScreenshot);

const BigGraphScreenshot = ({
    graphType,
    graphData,
    graphOf,
    onLoad = () => { },
    previousDayStat = [] as any
}) => {

    const darkModeTitleColor = useColorModeValue("gray.700", "gray.700");
    const [barGraphData, setBarGraphData] = useState<any>([...graphData]);
    const [tickFormatDivisor, setTickFormatDivisor] = useState(7);

    const markLineCss = {
        "display": "inline",
        "cursor": "pointer",
    }

    const calculateBarGraphDataPoints =
        useCallback(
            () => {
                let barData = [...graphData]
                barData = barData.map(
                    (dataPoint, i, arr) => {
                        let newDataPoint = { ...dataPoint }
                        newDataPoint.y = (i == 0)
                            ? dataPoint?.y - previousDayStat[previousDayStat?.length - 1]?.followerCount || 0
                            : dataPoint?.y - arr[i - 1]?.y;
                        return newDataPoint;
                    }
                )
                return barData;
            }, [graphData, graphOf, previousDayStat]
        );

    useEffect(() => {
        graphOf?.toLowerCase() == "followers" && graphType == "bar" && setBarGraphData([...calculateBarGraphDataPoints()]);

        graphOf?.toLowerCase() != "followers" && graphType == "bar" && setBarGraphData([...graphData]);
        onLoad();
    }, [graphOf, graphType]);

    useEffect(() => {
        const tickFormatDivisor = barGraphData?.lenght > 32 && barGraphData?.length < 99 ? 7 : 30;
        setTickFormatDivisor(tickFormatDivisor);
    }, [graphOf, barGraphData, graphData])

    return (
        <Box
            display="flex"
            flexDirection="column"
            bg="white"
            placeItems={"center"}
            m={"auto"}
            rounded={"2xl"}
            p={10}
            w={"90%"}
        >
            <Heading p={3} fontSize={"3xl"} color={darkModeTitleColor}>
                {(graphOf.toLowerCase() == "followers") ? "Follower Growth" : graphOf}
            </Heading>
            {
                (graphType == "line") ? (
                    <>
                        <XYPlot
                            height={330}
                            width={990}
                            xType="ordinal"
                            yDomain={yDomain(graphData)}
                            margin={{ left: 0, right: 0, top: 10, bottom: 0 }}
                            className="axesClass"
                        >
                            <GradientDefs>
                                <linearGradient id="CoolGradient" x1="0" x2="0" y1="0" y2="1">
                                    <stop offset="0%" stopColor="#11BB8D" stopOpacity={0.4} />
                                    <stop offset="100%" stopColor="#93eed5" stopOpacity={0.3} />
                                </linearGradient>
                            </GradientDefs>
                            <AreaSeries
                                data={graphData}
                                opacity={1}
                                style={{
                                    fill: "url(#CoolGradient)",
                                    stroke: '#11BB8D',
                                    strokeWidth: '0px'
                                }}
                            />
                            <MarkSeries
                                data={graphData}
                                color="#11BB8D"
                                size="4"
                                style={markLineCss}
                            />
                        </XYPlot>
                        <Flex w="990px" direction={"row"} ml={(graphData.length > 29) ? 10 : 40}>
                            {
                                graphData.map((dataPoint, i) => {
                                    if (graphData.length < 8) {
                                        return <><Text fontSize={10} w={"42px"}>{moment(dataPoint?.date, "MMM. Do YY").format("DD/MM").toString()}</Text><Spacer /></>
                                    } else {
                                        return (i % tickFormatDivisor == 0)
                                            ? <><Text fontSize={"sm"} w={"42px"} key={i}>{moment(dataPoint?.date, "MMM. Do YY").format("DD/MM").toString()}</Text><Spacer /></>
                                            : null
                                    }
                                })
                            }
                        </Flex>
                    </>
                )
                    :
                    (
                        <>
                            <XYPlot
                                height={330}
                                width={990}
                                xType="ordinal"
                                yDomain={yDomain(barGraphData)}
                                margin={{ left: 0, right: 0, top: 10, bottom: 0 }}
                                w="100%"
                            >
                                <GradientDefs>
                                    <linearGradient id="CoolGradient" x1="0" x2="0" y1="0" y2="1">
                                        <stop offset="0%" stopColor="#11BB8D" stopOpacity={0.4} />
                                        <stop offset="100%" stopColor="#93eed5" stopOpacity={0.3} />
                                    </linearGradient>
                                </GradientDefs>
                                <VerticalBarSeries
                                    data={barGraphData}
                                    opacity={1}
                                    color="#11BB8D"
                                    style={{
                                        fill: "url(#CoolGradient)",
                                        stroke: '#11BB8D',
                                        strokeWidth: '0px',
                                        cursor: "pointer",
                                    }}
                                    barWidth={0.60}
                                />
                            </XYPlot>
                            <Flex w="990px" direction={"row"} ml={(barGraphData.length > 29) ? 10 : 40}>
                                {
                                    barGraphData.map((dataPoint, i) => {
                                        if (barGraphData.length < 8) {
                                            return <><Text fontSize={10} w={"42px"} key={i}>{moment(dataPoint?.date, "MMM. Do YY").format("DD/MM").toString()}</Text><Spacer /></>
                                        } else {
                                            return (i % tickFormatDivisor == 0)
                                                ? <><Text fontSize={"sm"} w={"42px"} key={i}>{moment(dataPoint?.date, "MMM. Do YY").format("DD/MM").toString()}</Text><Spacer /></>
                                                : null
                                        }
                                    })
                                }
                            </Flex>
                        </>
                    )
            }
        </Box>
    );
}

const BigGraphScreenshotMemoed = memo(BigGraphScreenshot);